import React, { useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import { userManager, isAuthorised, signIn, signOut, REQUIRED_GROUP } from '../lib/auth';

/**
 * Gates everything behind a Cognito sign-in.
 *
 * Three outcomes:
 *   not signed in       -> redirect to the hosted UI
 *   signed in, no group -> refuse, with a sign-out so they can try another
 *                          account rather than being stuck
 *   signed in + group   -> render the dashboard
 *
 * The group check is a convenience for the user, not the security boundary. The
 * campaign service verifies the JWT and the group itself, so editing this
 * component in devtools gets you a rendered page and 403s from every request.
 */
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    userManager
      .getUser()
      .then((u) => {
        if (cancelled) return;
        if (!u || u.expired) {
          signIn().catch(() => setChecked(true));
          return;
        }
        setUser(u);
        setChecked(true);
      })
      .catch(() => !cancelled && setChecked(true));

    const onExpired = () => signIn().catch(() => {});
    userManager.events.addAccessTokenExpired(onExpired);

    return () => {
      cancelled = true;
      userManager.events.removeAccessTokenExpired(onExpired);
    };
  }, []);

  if (!checked) {
    return <div style={styles.centre}>Checking your sign-in…</div>;
  }

  if (!user) {
    return (
      <div style={styles.centre}>
        <p>Redirecting you to sign in…</p>
        <button style={styles.button} onClick={() => signIn()}>
          Sign in
        </button>
      </div>
    );
  }

  if (!isAuthorised(user)) {
    return (
      <div style={styles.centre}>
        <h2>Not authorised</h2>
        <p>
          {String(user.profile?.email ?? 'This account')} is signed in but is not a member of{' '}
          <code>{REQUIRED_GROUP}</code>, so it cannot use the campaign dashboard.
        </p>
        <p style={styles.muted}>Ask an administrator to add you, or sign in with another account.</p>
        <button style={styles.button} onClick={() => signOut()}>
          Sign out
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const styles: Record<string, React.CSSProperties> = {
  centre: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: 12,
    fontFamily: 'system-ui, sans-serif',
    textAlign: 'center',
    padding: 24
  },
  muted: { color: '#6b7280', fontSize: 14 },
  button: {
    padding: '8px 18px',
    borderRadius: 6,
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default AuthGuard;
